import React from "react";

export type OptionalExceptFor<T, TRequired extends keyof T, TPartial = Partial<T>> = TPartial & Pick<T, TRequired>;

export type AddRequiredProperties<T, TRequired extends keyof T> = Omit<T, TRequired> & Pick<T, TRequired>;

export type TupleToUnion<TArray extends readonly unknown[]> = TArray extends [infer First, ...infer Rest] ? First | TupleToUnion<Rest> : never;

export type DeepPartial<TType> = TType extends Function
    ? TType
    : TType extends Array<infer InferredArrayMember>
    ? DeepPartialArray<InferredArrayMember>
    : TType extends object
    ? DeepPartialObject<TType>
    : TType | undefined;

type DeepPartialArray<TType> = Array<DeepPartial<TType>>;

type DeepPartialObject<TType> = { [Key in keyof TType]?: DeepPartial<TType[Key]> };

/**
 * Makes all the properties in the given type optional, except for the given required type
 * @example
 * type Test = { id: number; properties: { name: string; age: number } };
 * type TestPartial = DeepPartialWithRequired<Test, { id: number, properties: { name: string } }>;
 * // TestPartial = { id: number; properties: { name: string; age?: number; } }
 * */
export type DeepPartialWithRequired<TType, TRequired extends DeepPartial<TType>> = DeepPartial<TType> & TRequired;

// eslint-disable-next-line prettier/prettier
export type DotNotatedStringToUnion<T extends string> = T extends `${infer First}.${infer Rest}` ? T | First | DotNotatedStringToUnion<Rest> : T;

export type TupleOfDotNotatedStringsToUnion<TArray extends readonly string[]> = TArray extends [infer First, ...infer Rest] ? First extends string ? Rest extends string[] ? DotNotatedStringToUnion<First> | TupleOfDotNotatedStringsToUnion<Rest> : never : never : never;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type ExtractRefFromComponent<TComponent extends React.ComponentType<any>> = TComponent extends (props: unknown & React.RefAttributes<infer TObject>) => React.ReactElement<unknown, string | React.JSXElementConstructor<unknown>> ? TObject : never;

/** Returns Union of key value tuples.
 * @example type ObjKeyValues = ObjectEntries<{ id: number; name: string }> = ["id", number] | ["name", string]
 */
export type ObjectEntries<TObject extends object, TKey = keyof TObject> = TKey extends keyof TObject
  ? [TKey, [TObject[TKey]] extends [undefined] ? TObject[TKey] : Exclude<TObject[TKey], undefined>]
  : never
